import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { dashboardAPI, companyAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Users,
  UserCheck,
  UserX,
  Calendar,
  Clock,
  AlertTriangle,
  FileText
} from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const [dashRes, compRes] = await Promise.all([
        dashboardAPI.getCompanyDashboard(user.company_id),
        companyAPI.getOne(user.company_id)
      ]);
      setData(dashRes.data);
      setCompany(compRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout title="Admin Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const { stats } = data || {};
  const attendanceRate = stats?.total_employees > 0 
    ? Math.round((stats.present_today / stats.total_employees) * 100) 
    : 0;

  return (
    <DashboardLayout 
      title="Admin Dashboard" 
      subtitle={company?.name}
    >
      {/* Feature Flags Alert */}
      {company?.feature_flags && (
        <div className="mb-6 p-4 bg-secondary rounded-lg">
          <p className="text-sm font-medium mb-2">Active Modules:</p>
          <div className="flex flex-wrap gap-2">
            {company.feature_flags.attendance_enabled && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Attendance</Badge>
            )}
            {company.feature_flags.payroll_enabled && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Payroll</Badge>
            )}
            {company.feature_flags.time_tracking_enabled && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Time Tracking</Badge>
            )}
            {company.feature_flags.audit_enabled && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Audit</Badge>
            )}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card className="stat-card" data-testid="admin-total-employees">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_employees || 0}</p>
                <p className="text-xs text-muted-foreground">Total Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.present_today || 0}</p>
                <p className="text-xs text-muted-foreground">Present</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.absent_today || 0}</p>
                <p className="text-xs text-muted-foreground">Absent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.late_today || 0}</p>
                <p className="text-xs text-muted-foreground">Late</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.on_leave_today || 0}</p>
                <p className="text-xs text-muted-foreground">On Leave</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_overtime_hours || 0}h</p>
                <p className="text-xs text-muted-foreground">OT Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Rate Card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Today&apos;s Attendance Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p className="text-5xl font-bold text-primary mb-2">{attendanceRate}%</p>
              <Progress value={attendanceRate} className="h-3 mb-4" />
              <p className="text-sm text-muted-foreground">
                {stats?.present_today} of {stats?.total_employees} employees present
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <span>Leave Requests</span>
                </div>
                <Badge variant={stats?.pending_leaves > 0 ? "destructive" : "secondary"}>
                  {stats?.pending_leaves || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-purple-500" />
                  <span>Shift Changes</span>
                </div>
                <Badge variant={stats?.pending_shift_changes > 0 ? "destructive" : "secondary"}>
                  {stats?.pending_shift_changes || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
